import React from 'react';
import { useSelector } from 'react-redux';
import { Box, InputAdornment, MenuItem, Paper, Select, TablePagination, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { GridSortModel } from '@mui/x-data-grid-pro';
import { format } from 'date-fns';

import { ApplicationState } from 'store/store';
import { Option } from 'lib/types';
import { GridStorageName } from 'lib/MuiGrid/StateController';
import { MuiGrid } from 'lib/MuiGrid/MuiGrid';
import {
    ANY_RATING,
    CustomerReviewsFilter,
    ratingOptions,
    useCustomerReviewsData
} from 'components/reviews/hooks/useCustomerReviewsData';
import { GridLocationFilter } from 'lib/grid/GridLocationFilter';
import { DebouncedGridFilterInput } from 'lib/grid/DebouncedGridFilterInput';

export const CustomerReviews = () => {
    const theme = useTheme();
    const { locations } = useSelector((state: ApplicationState) => state.locations);
    const [filter, setFilter] = React.useState<CustomerReviewsFilter>({});
    const {
        pagedReviews: reviews,
        loading,
        onPageChange,
        page,
        rowsPerPage,
        count,
        columns
    } = useCustomerReviewsData(filter);

    const handleSearch = React.useCallback(
        (value?: string) => {
            setFilter({ ...filter, text: value });
        },
        [filter]
    );

    const handleLocationChange = React.useCallback(
        (value?: string) => {
            setFilter({ ...filter, locationId: value });
        },
        [filter]
    );

    const handleRatingChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setFilter({ ...filter, rating: value === ANY_RATING ? undefined : Number(value) });
        },
        [filter]
    );

    const handleSortChange = React.useCallback(
        (model: GridSortModel) => {
            if (model && model[0]) {
                setFilter({ ...filter, sortModel: model });
            } else {
                setFilter({ ...filter, sortModel: undefined });
            }
        },
        [filter]
    );

    const renderOption = React.useCallback(
        (option: Option) => (
            <MenuItem key={option.value} value={option.value}>
                {option.label}
            </MenuItem>
        ),
        []
    );

    const Pagination = React.useCallback(
        () => (
            <TablePagination
                count={count}
                onPageChange={onPageChange}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
                component="div"
                sx={{
                    '&:last-child': {
                        paddingRight: theme.spacing(6)
                    }
                }}
            />
        ),
        [onPageChange, page, count, rowsPerPage, theme]
    );

    const rows = React.useMemo(
        () =>
            reviews.map(review => {
                const location = locations.find(({ _id }) => _id === review.locationId);
                const updatedAt = format(new Date(review.updatedAt), 'dd/MM/yyyy ');
                return {
                    id: review.id,
                    rating: review.rating,
                    orderId: review.orderId,
                    fullName: review.fullName,
                    userId: review.userId,
                    text: review.text,
                    platform: review.source.platform,
                    location: location?.title || review.locationId,
                    updatedAt
                };
            }),
        [reviews, locations]
    );
    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ flexBasis: 'max-content', height: '100%', overflow: 'hidden' }}
            paddingTop={2}
        >
            <Box
                display="flex"
                flexDirection="row"
                gap={2}
                alignItems="center"
                justifyContent="space-between"
            >
                <DebouncedGridFilterInput
                    value={filter.text}
                    onChange={handleSearch}
                    name="text"
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="Search reviews by comment"
                    horizontalPadding={false}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }
                    }}
                />
                <Box display="flex" flexDirection="row" gap={1}>
                    <GridLocationFilter
                        value={filter.locationId}
                        onChange={handleLocationChange}
                        size="small"
                    />
                    <Select
                        value={filter.rating ? String(filter.rating) : ANY_RATING}
                        onChange={handleRatingChange}
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 200 }}
                    >
                        {ratingOptions.map(renderOption)}
                    </Select>
                </Box>
            </Box>
            <Paper sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                <MuiGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    disableMultipleRowSelection
                    onSortModelChange={handleSortChange}
                    hideFooter={false}
                    rowCount={count}
                    disableColumnFilter
                    localeText={{ noRowsLabel: 'No reviews found' }}
                    storageName={GridStorageName.CustomerReviews}
                    disableRowSelectionOnClick={false}
                    pagination
                    paginationMode="server"
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                id: false
                            }
                        }
                    }}
                    slots={{
                        pagination: Pagination
                    }}
                />
            </Paper>
        </Box>
    );
};
